"use client";

import { useMemo, useRef, useState } from "react";
import { RiCloseLine, RiSearchLine } from "react-icons/ri";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from "@/components/ui";

import { cx } from "@/lib/tailwind-utils";

import {
  SuggestionItem,
  SuggestionType,
  suggestionPickerText,
} from "@/types/suggestions";

import { TYPE_LABELS, groupSuggestions } from "./suggestion";

function filterSuggestions(
  suggestions: SuggestionItem[],
  query: string,
): SuggestionItem[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return suggestions;
  }
  return suggestions.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.type.toLowerCase().includes(q) ||
      item.content.toLowerCase().includes(q),
  );
}

interface TemplateVariableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestions: SuggestionItem[];
  onSelect: (item: SuggestionItem) => void;
  /** Dialog heading; defaults to "Insert variable". */
  title?: string;
  /** Picking inserts the item's text rather than a pill, so rows show that text. */
  insertsText?: boolean;
}

/**
 * Presentational "Select a token" dialog: a search box plus a scrollable,
 * grouped list of template variables. Reuses the existing {@link SuggestionItem}
 * data and grouping ({@link groupSuggestions}/{@link TYPE_LABELS}) so headers and
 * order stay a single source of truth with the `@`-mention popup.
 */
/** Whether an item's content is nothing but its own `{token}`.
 *
 * `content` is what insertion writes, and on most surfaces it carries real information —
 * a canned chat reply, an SMS snippet — worth previewing under the label. For a template
 * variable it is the brace form: the storage an author never types and never sees again
 * once the pill is in. Printing it teaches the one syntax the `@` menu exists to replace,
 * so those rows show the label alone.
 */
function isBareToken(content: string): boolean {
  return /^\{[a-z0-9_.]+\}$/i.test(content.trim());
}

export function TemplateVariableModal({
  open,
  onOpenChange,
  suggestions,
  onSelect,
  title = "Insert variable",
  insertsText = false,
}: TemplateVariableModalProps) {
  const [query, setQuery] = useState("");
  // -1 = nothing highlighted. Since a click inserts immediately (there's no
  // browse-then-confirm step), we never pre-highlight a row; the highlight only
  // appears transiently while navigating with the arrow keys.
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const selectedRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(
    () => filterSuggestions(suggestions, query),
    [suggestions, query],
  );
  const grouped = useMemo(() => groupSuggestions(filtered), [filtered]);
  const orderedItems = useMemo(
    () => grouped.flatMap(([, items]) => items),
    [grouped],
  );
  const itemIndexMap = useMemo(
    () => new Map(orderedItems.map((item, idx) => [item.id, idx])),
    [orderedItems],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Reset so the next open starts fresh (component stays mounted).
      setQuery("");
      setSelectedIndex(-1);
    }
    onOpenChange(next);
  };

  const scrollSelectedIntoView = () => {
    // Defer so the ref points at the newly-selected row.
    const run = () => {
      const el = selectedRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    };
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(run);
    } else {
      run();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (orderedItems.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      // From -1 (nothing highlighted) this lands on the first row.
      setSelectedIndex((prev) => (prev + 1) % orderedItems.length);
      scrollSelectedIntoView();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev <= 0 ? orderedItems.length - 1 : prev - 1,
      );
      scrollSelectedIntoView();
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = orderedItems[selectedIndex];
      if (item) {
        onSelect(item);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader className="gap-y-3 pb-3 md:pb-3">
          <div className="flex items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            <DialogClose
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-background-secondary hover:text-foreground"
              aria-label="Close"
            >
              <RiCloseLine className="size-5" />
            </DialogClose>
          </div>
          <Input
            autoFocus
            icon={RiSearchLine}
            placeholder="Search variables..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
          />
        </DialogHeader>
        <div className="max-h-[min(400px,50vh)] overflow-auto border-t border-border px-4 md:px-6">
          {orderedItems.length === 0 ? (
            <div className="px-4 py-6 text-center text-body text-muted-foreground">
              No variables found.
            </div>
          ) : (
            grouped.map(([type, typeItems], groupIndex) => (
              <div key={type} className="flex flex-col">
                <div className="sticky top-0 z-10 bg-background-secondary px-4 py-1">
                  <span className="text-caption-strong text-muted-foreground">
                    {TYPE_LABELS[type as SuggestionType]}
                  </span>
                </div>
                {typeItems.map((item, index) => {
                  const itemIndex = itemIndexMap.get(item.id) ?? -1;
                  const isSelected = itemIndex === selectedIndex;
                  // A row that still carries a snippet under its label is two
                  // lines tall, and wants the taller padding; a template
                  // variable, whose token is not printed, is one line.
                  const preview = suggestionPickerText(item, { insertsText });
                  const showsSnippet = !isBareToken(preview);
                  return (
                    <button
                      ref={isSelected ? selectedRef : undefined}
                      key={item.id}
                      type="button"
                      onClick={() => onSelect(item)}
                      // Hover shares the same highlight as the arrow-key cursor,
                      // so mouse and keyboard drive one selection.
                      onMouseEnter={() => setSelectedIndex(itemIndex)}
                      className={cx(
                        "flex w-full flex-col items-start border-b border-border px-4 text-left transition-colors duration-100",
                        showsSnippet ? "py-3" : "py-2.5",
                        isSelected
                          ? "bg-primary/90 *:!text-primary-foreground"
                          : "bg-background/90",
                        index === typeItems.length - 1 &&
                          groupIndex !== grouped.length - 1 &&
                          "border-b-0",
                      )}
                    >
                      <p className="text-s font-medium text-foreground">
                        {item.label}
                      </p>
                      {showsSnippet && (
                        <p className="line-clamp-2 whitespace-pre-wrap break-words text-caption leading-snug text-muted-foreground">
                          {preview}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
